import React from 'react';
import { FieldArray, FormikProps } from 'formik';
import COButton from 'shared/components/COButton';
import InputField from 'shared/components/InputField';
import COCheckbox from 'shared/components/COCheckbox';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';
import { LeftCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import styles from './ProductDistribution.module.scss';
import {
  ButtonLabels,
  ProductDistributionProps,
} from 'enums/ProductDistributionEnums';
import { ProcessingFacilityFieldEnums } from 'enums/ProcessingFacilityEnums';
import { ProductDistributionFormData } from 'models/ProductDistributionFormData/ProductDistributionFormData.model';

interface ProductDistributionFormProps {
  formikProps: FormikProps<ProductDistributionFormData>;
  isDisabled: boolean;
  buttonLoading: boolean;
  draftButtonLoading: boolean;
  handleGoBack: () => void;
  handleTopBackButton: () => void;
  handleOpenCancelModal: () => void;
  validateForHandleSave: (
    formikProps: FormikProps<ProductDistributionFormData>,
    handleSaveAsDraftSubmission: (values: ProductDistributionFormData) => void
  ) => void;
  handleSaveAsDraftSubmission: (values: ProductDistributionFormData) => void;
  showLocationModal: (index: number) => void;
  fetchData: (params: { 'input-field': string }) => void;
}

const ProductDistributionForm: React.FC<ProductDistributionFormProps> = ({
  formikProps,
  isDisabled,
  buttonLoading,
  draftButtonLoading,
  handleGoBack,
  handleTopBackButton,
  handleOpenCancelModal,
  validateForHandleSave,
  handleSaveAsDraftSubmission,
  showLocationModal,
  fetchData,
}) => {
  return (
    <FieldArray name={ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}>
      {arrayHelpers => (
        <div>
          <div className={styles.formHeaderContainer}>
            <div className={styles.formHeader}>
              <p className={styles.backButton} onClick={handleGoBack}>
                <LeftCircleOutlined />
              </p>
              <h2>{ProductDistributionProps.DISTRIBUTION_HEADING}</h2>
            </div>
            <div className={styles.formButtonContainer}>
              {isDisabled === false && (
                <COButton
                  text
                  disabled={isDisabled}
                  onClick={() =>
                    arrayHelpers.push({
                      endMarketLocation: '',
                      units: '',
                      transportationAir: false,
                    })
                  }>
                  <AddIcon />
                  {ButtonLabels.ADD_END_MARKET_LOCATION}
                </COButton>
              )}
              <COButton submit loading={buttonLoading}>
                {ButtonLabels.NEXT}
                <ArrowRightOutlined />
              </COButton>
            </div>
          </div>
          <div className={styles.easeDiv}>
            {isDisabled === true ? (
              <div
                className={clsx({
                  [styles.topRightContainer]: true,
                  [styles.backDisabledState]: isDisabled,
                })}>
                <COButton text onClick={handleTopBackButton}>
                  Back
                </COButton>
              </div>
            ) : (
              <div className={styles.topRightContainer}>
                <COButton text onClick={handleOpenCancelModal}>
                  {ButtonLabels.CANCEL}
                </COButton>
                <COButton
                  secondary
                  loading={draftButtonLoading}
                  onClick={() =>
                    validateForHandleSave(
                      formikProps,
                      handleSaveAsDraftSubmission
                    )
                  }>
                  {ButtonLabels.SAVE_AS_DRAFT}
                </COButton>
              </div>
            )}
          </div>
          <div className={styles.topFieldRowContainer}>
            <div className={styles.topFieldRow}>
              <div className={styles.topField}>
                <InputField
                  name={ProcessingFacilityFieldEnums.TOTAL_PERIOD_UNITS_NAME}
                  placeholderstring={
                    ProcessingFacilityFieldEnums.TOTAL_PERIOD_UNITS_PLACEHOLDER
                  }
                  disabled={true}
                  label={
                    ProcessingFacilityFieldEnums.TOTAL_PERIOD_UNITS_PLACEHOLDER
                  }
                />
              </div>
              <div className={styles.topField}>
                <InputField
                  name={
                    ProcessingFacilityFieldEnums.PROCESSING_FACILITY_TOTAL_NAME
                  }
                  placeholderstring={
                    ProcessingFacilityFieldEnums.PROCESSING_FACILITY_TOTAL_PLACEHOLDER
                  }
                  disabled={isDisabled}
                  label={
                    ProcessingFacilityFieldEnums.PROCESSING_FACILITY_TOTAL_PLACEHOLDER
                  }
                />
              </div>
              <div className={styles.topField}>
                <InputField
                  name={ProcessingFacilityFieldEnums.TOTAL_UNIT_NAME}
                  placeholderstring={
                    ProcessingFacilityFieldEnums.TOTAL_UNIT_PLACEHOLDER
                  }
                  disabled={isDisabled}
                  label={ProcessingFacilityFieldEnums.TOTAL_UNIT_LABEL}
                  onFocus={() =>
                    fetchData({
                      'input-field': 'goods stored in processing facility',
                    })
                  }
                />
              </div>
              <div className={styles.topField}>
                <InputField
                  name={ProcessingFacilityFieldEnums.ONLINE_RETAIL_NAME}
                  placeholderstring={
                    ProcessingFacilityFieldEnums.ONLINE_RETAIL_PLACEHOLDER
                  }
                  disabled={isDisabled}
                  label={ProcessingFacilityFieldEnums.ONLINE_RETAIL_PLACEHOLDER}
                  onFocus={() =>
                    fetchData({
                      'input-field': 'goods sold via online retail',
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className={styles.ingredientsFormContainer}>
            {formikProps?.values?.productDistributionRoute?.map((_, index) => (
              <div key={index} className={styles.fieldRow}>
                <div>
                  <p className={styles.pointer}>{index + 1}</p>
                </div>
                <div className={styles.bigFieldContainer}>
                  <InputField
                    name={`${ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}[${index}].${ProcessingFacilityFieldEnums.END_MARKET_LOCATION_NAME}`}
                    placeholderstring={
                      ProcessingFacilityFieldEnums.END_MARKET_LOCATION_PLACEHOLDER
                    }
                    label={
                      ProcessingFacilityFieldEnums.END_MARKET_LOCATION_PLACEHOLDER
                    }
                    disabled={isDisabled}
                    onFocus={() => {
                      showLocationModal(index);
                      fetchData({
                        'input-field': 'end market location',
                      });
                    }}
                    suffix={
                      <span
                        onClick={() => !isDisabled && showLocationModal(index)}>
                        <MapSearch />
                      </span>
                    }
                  />
                </div>
                <div className={styles.topField}>
                  <InputField
                    name={`${ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}[${index}].${ProcessingFacilityFieldEnums.UNITS_NAME}`}
                    placeholderstring={
                      ProcessingFacilityFieldEnums.UNITS_PLACEHOLDER
                    }
                    label={ProcessingFacilityFieldEnums.UNITS_PLACEHOLDER}
                    disabled={isDisabled}
                    onFocus={() =>
                      fetchData({
                        'input-field': 'goods sent to this end market',
                      })
                    }
                  />
                </div>
                <div className={styles.checboxContainer}>
                  <COCheckbox
                    name={`${ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}[${index}].${ProcessingFacilityFieldEnums.TRANSPORTATION_AIR_NAME}`}
                    disabled={isDisabled}
                    onChange={e =>
                      formikProps.setFieldValue(
                        `${ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}[${index}].${ProcessingFacilityFieldEnums.TRANSPORTATION_AIR_NAME}`,
                        e.target.checked
                      )
                    }
                    label={
                      <div className={styles.checkboxLabel}>
                        <p>
                          {
                            ProcessingFacilityFieldEnums.TRANSPORTATION_AIR_PLACEHOLDER
                          }
                        </p>
                      </div>
                    }
                  />
                </div>
                {(formikProps?.values?.productDistributionRoute?.length ?? 0) >
                  1 &&
                  !isDisabled && (
                    <div
                      className={styles.deleteButton}
                      onClick={() => arrayHelpers.remove(index)}>
                      <DeleteButton />
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}
    </FieldArray>
  );
};

export default ProductDistributionForm;
